import type {
  GroupApiProps,
  GroupAuditLogData,
  GroupAuditLogResponse,
  GroupItem,
  GroupStructure,
  GroupWraper,
  GroupsResponse,
} from '../../@types/groups';
import { createFieldFilterText } from '../../utils/filter-url-builder-utils';
import { createGroupData } from '../../utils/group-functions';
import { createCustomSortWithOrderBy } from '../../utils/sort-url-builder-utils';
import { api } from './api';

export const groupApi = api.enhanceEndpoints({ addTagTypes: ['Group', 'GroupAuditLog'] }).injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getGroups: builder.query<Array<GroupStructure>, GroupApiProps>({
      query: (args) =>
        `/groups/simple?externalUserSignupRoles=${args.externalUserSignupRoles}${createFieldFilterText({
          filters: args.filters,
        })}${createCustomSortWithOrderBy(args.sortedFields)}`,
      transformResponse: (response: GroupsResponse) => response?.items?.map((item) => createGroupData(item)) || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Group' as const, id: item.original.id })),
              { type: 'Group' as const, id: 'LIST' },
            ]
          : [{ type: 'Group' as const, id: 'LIST' }],
    }),
    getGroupById: builder.query<GroupStructure, unknown>({
      query: (id: string) => `/groups/simple/${id}`,
      transformResponse: (response: GroupWraper) => createGroupData(response.result),
      providesTags: (result) => (result ? [{ type: 'Group' as const, id: result.original.id }] : ['Group']),
    }),
    updateGroup: builder.mutation<void, Pick<GroupItem, 'id'> & Partial<GroupItem>>({
      query: ({ id, ...patch }) => ({
        url: `/groups/simple/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, arg) => {
        if (result) {
          return [
            { type: 'Group', id: arg.id },
            { type: 'GroupAuditLog', id: `LIST-${arg.id}` },
          ];
        } else {
          return [];
        }
      },
    }),
    getGroupAuditLog: builder.query<Array<GroupAuditLogData>, string>({
      query: (id: string) => `/groups/${id}/audit-log`,
      transformResponse: (response: GroupAuditLogResponse) => response.result ?? [],
      providesTags: (result, _error, id) =>
        result
          ? [
              ...result.map((item) => ({ type: 'GroupAuditLog' as const, id: `${id}-${item.id}` })),
              { type: 'GroupAuditLog' as const, id: `LIST-${id}` },
            ]
          : [{ type: 'GroupAuditLog' as const, id: `LIST-${id}` }],
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useLazyGetGroupByIdQuery,
  useUpdateGroupMutation,
  useLazyGetGroupAuditLogQuery,
} = groupApi;
